// Browser push (PLAN §8.7: VAPID, secure contexts only; on an iPad only once the app is
// installed with Add to Home Screen). The service worker is /sw.js (web/public).

import { t } from "../i18n";

export type PushSupport =
  | { ok: true }
  | { ok: false; reason: "insecure" | "install" | "unsupported" | "no-key" };

type Env = {
  secure: boolean;
  serviceWorker: boolean;
  pushManager: boolean;
  notifications: boolean;
  /** An iPhone or iPad (iPadOS says "Macintosh" but has touch). */
  ios: boolean;
  /** An Android phone or tablet. */
  android?: boolean;
  /** Opened from the Home Screen (display-mode standalone). */
  standalone: boolean;
};

/** Where to allow notifications again once the device blocked them (R10.9-B2): the installed
 *  app has no browser site settings to point at. */
export function deniedHelp(env: Pick<Env, "ios" | "android" | "standalone">): string {
  if (env.standalone && env.ios) return t("shell.pushDeniedIos");
  if (env.standalone && env.android) return t("shell.pushDeniedAndroid");
  return t("shell.pushDenied");
}

/** Whether this device can get push, and if not, why (the /me page explains it). */
export function pushSupport(env: Env, publicKey: string | null | undefined): PushSupport {
  if (!env.secure) return { ok: false, reason: "insecure" };
  if (!env.serviceWorker || !env.pushManager || !env.notifications) {
    return { ok: false, reason: env.ios && !env.standalone ? "install" : "unsupported" };
  }
  if (!publicKey) return { ok: false, reason: "no-key" };
  return { ok: true };
}

/** Why not, said in the interface language (each one read when it's used). */
export const PUSH_REASON: Record<Exclude<PushSupport, { ok: true }>["reason"], string> = {
  get insecure() {
    return t("shell.pushInsecure");
  },
  get install() {
    return t("shell.pushInstall");
  },
  get unsupported() {
    return t("shell.pushUnsupported");
  },
  get "no-key"() {
    return t("shell.pushNoKey");
  },
};

/** This browser, as `pushSupport` reads it. */
export function browserEnv(): Env {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const ua = nav?.userAgent ?? "";
  const ios =
    /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1);
  const standalone =
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    Boolean((nav as { standalone?: boolean } | undefined)?.standalone);
  return {
    secure: typeof window !== "undefined" && window.isSecureContext,
    serviceWorker: Boolean(nav && "serviceWorker" in nav),
    pushManager: typeof window !== "undefined" && "PushManager" in window,
    notifications: typeof window !== "undefined" && "Notification" in window,
    ios,
    android: /Android/.test(ua),
    standalone,
  };
}

/** A base64url VAPID key → the bytes PushManager.subscribe wants. */
export function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (base64url.length % 4)) % 4);
  const b64 = (base64url + pad).replaceAll("-", "+").replaceAll("_", "/");
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** A subscription → `POST /api/push/subscribe` body; null when the browser left a part out. */
export function subscribeBody(
  json: PushSubscriptionJSON,
): { endpoint: string; keys: { p256dh: string; auth: string } } | null {
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) return null;
  return { endpoint, keys: { p256dh, auth } };
}

/** The service worker (it shows pushes and opens the page a notification points to): register
 *  is idempotent, and `ready` waits until it's active, which subscribing needs. */
export async function registration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return navigator.serviceWorker.ready;
}

/** This device's subscription, if it has one. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Whether a subscription was made with this server key (a rotated VAPID key needs a new one:
 *  pushes signed with the new key are refused for the old subscription). Unknown = same. */
export function sameKey(key: ArrayBuffer | null | undefined, publicKey: string): boolean {
  if (!key) return true;
  const a = new Uint8Array(key);
  const b = keyBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Ask for permission (it must follow a tap: iOS insists) and subscribe this device. `fresh`:
 *  drop whatever this browser holds and make a new one (it belonged to someone else whose
 *  session ended without signing out; the server refuses to hand it over). */
export async function subscribeDevice(publicKey: string, fresh = false): Promise<PushSubscription> {
  if ("Notification" in window && Notification.permission === "default") {
    const answer = await Notification.requestPermission();
    if (answer !== "granted") throw new PushDenied();
  }
  if ("Notification" in window && Notification.permission === "denied") throw new PushDenied();
  const reg = await registration();
  const old = await reg.pushManager.getSubscription();
  if (old && !fresh && sameKey(old.options.applicationServerKey, publicKey)) return old;
  if (old) await old.unsubscribe().catch(() => false);
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(publicKey),
  });
}

/** Signing out: this device stops getting the person's pushes (`remove` tells the server).
 *  Best effort and quick: it never holds up the sign-out. */
export async function dropDevice(remove: (endpoint: string) => Promise<unknown>): Promise<void> {
  const work = (async () => {
    const sub = await currentSubscription();
    if (!sub) return;
    await remove(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => false);
  })().catch(() => undefined);
  await Promise.race([work, new Promise((r) => setTimeout(r, 2000))]);
}

export class PushDenied extends Error {
  constructor() {
    super(deniedHelp(browserEnv()));
  }
}
